// Agenda: Dia, Semana e Mês com uma barra só no topo (Hoje, setas, período, filtro de parceira).
// Celular: semana em lista contínua e mês como mapa de ocupação. Computador: grade da semana e mês com os horários.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useApptActions } from '../app/actions.ts';
import { useEventDrag } from '../lib/useEventDrag.ts';
import { useWide } from '../app/desk.tsx';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { blocksOn, dayStats, durShort, endMin, freeOf, heat, hoursOf, isLive, weekStart, proColor, useBlocks, type Block } from '../lib/agenda.ts';
import { can, useAgenda, useMe, usePros, type AgendaItem, type Professional, type Settings } from '../lib/data.ts';
import { addDays, DOW_LONG, DOW_SHORT, dowIndex, first, hhmm, initials, longDate, MONTHS, toMin } from '../lib/format.ts';
import { Icon } from '../ui/Icon.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';
import { useClock } from './Hoje.tsx';

type Mode = 'dia' | 'semana' | 'mes';
const MODE_KEY = 'sublime-agenda-modo';
const ST: Record<string, string> = { pending: 'a confirmar', confirmed: 'confirmado', arrived: 'chegou', in_service: 'atendendo', completed: 'concluído', cancelled: 'cancelado', no_show: 'faltou' };
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const ddmm = (d: string) => `${Number(d.slice(8))}`;
const monthName = (d: string) => MONTHS[Number(d.slice(5, 7)) - 1]!;
// nas faixas estreitas da semana: "Carla D."
const shortName = (n: string) => { const p = n.trim().split(/\s+/); return p.length > 1 ? `${p[0]} ${p[p.length - 1]![0]}.` : n; };
const proName = (pros: Professional[], id: string) => first(pros.find((p) => p.id === id)?.name ?? '');

// No celular o título curto cabe numa linha ao lado do botão de agendar ("Seg, 19 de out").
function periodLabel(mode: Mode, day: string, short = false): string {
  const mon = (d: string) => (short ? monthName(d).slice(0, 3) : monthName(d));
  if (mode === 'dia') return short ? `${longDate(day).split(',')[0]!.slice(0, 3)}, ${Number(day.slice(8))} de ${mon(day)}` : longDate(day);
  if (short && mode === 'semana') { const ws = weekStart(day), we = addDays(ws, 6); return `${Number(ws.slice(8))} a ${Number(we.slice(8))} de ${mon(we)}`; }
  if (mode === 'mes') return `${cap(monthName(day))} de ${day.slice(0, 4)}`;
  const ws = weekStart(day), we = addDays(ws, 6);
  return ws.slice(5, 7) === we.slice(5, 7) ? `${ddmm(ws)} a ${ddmm(we)} de ${monthName(we)}` : `${ddmm(ws)} de ${monthName(ws)} a ${ddmm(we)} de ${monthName(we)}`;
}

export function Agenda() {
  const nav = useNav();
  const me = useMe().data!;
  const wide = useWide();
  const today = useClock().date;
  const [mode, setModeState] = useState<Mode>(() => { try { const m = localStorage.getItem(MODE_KEY); return m === 'semana' || m === 'mes' ? m : 'dia'; } catch { return 'dia'; } });
  // o dia aberto na agenda sobrevive ao recarregar, mas só no mesmo dia (depois volta para hoje)
  const [day, setDayState] = useState(() => {
    try { const j = JSON.parse(sessionStorage.getItem('sublime-agenda-dia') ?? 'null') as { on?: string; day?: string } | null; return nav.params.date ?? (j && j.on === today && typeof j.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(j.day) ? j.day : today); } catch { return nav.params.date ?? today; }
  });
  const setDay = (d: string) => { setDayState(d); try { sessionStorage.setItem('sublime-agenda-dia', JSON.stringify({ on: today, day: d })); } catch { /* sem armazenamento */ } };
  const [only, setOnly] = useState<string>('all');
  const setMode = (m: Mode) => { setModeState(m); try { localStorage.setItem(MODE_KEY, m); } catch { /* sem armazenamento */ } };
  const allPros = (usePros().data ?? []).filter((p) => p.active);
  const mine = can(me, 'agendaTodas') ? allPros : allPros.filter((p) => p.id === me.professionalId);
  const pros = only === 'all' ? mine : mine.filter((p) => p.id === only);
  const colors = useMemo(() => new Map(allPros.map((p, i) => [p.id, proColor(p, i)])), [allPros]);
  const step = (n: number) => setDay(mode === 'dia' ? addDays(day, n) : mode === 'semana' ? addDays(day, 7 * n) : addDays(`${day.slice(0, 7)}-15`, 30 * n).slice(0, 8) + '01');
  const openDay = (d: string) => { setDay(d); setMode('dia'); };


  // a altura da barra presa no topo: os cabeçalhos de dia e de parceira ficam presos logo abaixo dela
  const bar = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = bar.current;
    if (!el) return;
    const set = () => el.parentElement?.style.setProperty('--ag-bar-h', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const inPeriod = mode === 'dia' ? day === today : mode === 'semana' ? weekStart(day) === weekStart(today) : day.slice(0, 7) === today.slice(0, 7);
  return (
    <div className="view-enter ag-view ag2">
      <div className="hd"><div className="wordmark-mark" role="img" aria-label="Sublime Nail Design" /></div>
      <div className="ag-bar" ref={bar}>
        <h1 className="ag-label" title={periodLabel(mode, day)}>{periodLabel(mode, day, !wide)}</h1>
        <div className="ag-nav">
          <button className={'ag-today' + (inPeriod ? ' here' : '')} onClick={() => setDay(today)} title="Voltar para hoje (T)">Hoje</button>
          <button className="ag-arrow" aria-label={mode === 'dia' ? 'Dia anterior' : mode === 'semana' ? 'Semana anterior' : 'Mês anterior'} title="Anterior (←)" onClick={() => step(-1)}><Icon name="back" /></button>
          <button className="ag-arrow" aria-label={mode === 'dia' ? 'Próximo dia' : mode === 'semana' ? 'Próxima semana' : 'Próximo mês'} title="Próximo (→)" onClick={() => step(1)}><Icon name="chev" /></button>
        </div>
        <div className="seg ag-seg" role="tablist" aria-label="Visão da agenda">
          {(['dia', 'semana', 'mes'] as const).map((m) => <button key={m} role="tab" aria-selected={mode === m} className={mode === m ? 'active' : ''} title={`${{ dia: 'Dia', semana: 'Semana', mes: 'Mês' }[m]} (${m === 'mes' ? 'M' : m[0]!.toUpperCase()})`} onClick={() => setMode(m)}>{{ dia: 'Dia', semana: 'Semana', mes: 'Mês' }[m]}</button>)}
        </div>
        {mine.length > 1 && (
          <div className="ag-filter" role="radiogroup" aria-label="Parceiras">
            <button role="radio" aria-checked={only === 'all'} className={only === 'all' ? 'on' : ''} onClick={() => setOnly('all')}>Todas</button>
            {mine.map((p) => <button key={p.id} role="radio" aria-checked={only === p.id} className={only === p.id ? 'on' : ''} onClick={() => setOnly(only === p.id ? 'all' : p.id)}><i style={{ background: colors.get(p.id) }} />{first(p.name)}</button>)}
          </div>
        )}
        <button className="iconbtn ag-lock" aria-label="Bloquear horário ou registrar ausência" title="Bloquear horário ou ausência" onClick={() => nav.open({ kind: 'bloqueio', professionalId: only !== 'all' ? only : undefined, date: day >= today ? day : undefined })}><Icon name="lock" /></button>
        <button className="iconbtn brand ag-new" aria-label="Novo agendamento" onClick={() => nav.open({ kind: 'novo', date: day >= today ? day : undefined })}><Icon name="plusLg" /></button>
      </div>
      {mode === 'dia' && <CalDia day={day} today={today} pros={pros} colors={colors} wide={wide} />}
      {mode === 'semana' && (wide
        ? <SemanaGrade day={day} today={today} pros={pros} colors={colors} onDay={openDay} />
        : <SemanaLista day={day} setDay={setDay} today={today} pros={pros} colors={colors} />)}
      {mode === 'mes' && (wide
        ? <MesGrade day={day} today={today} pros={pros} colors={colors} onDay={openDay} />
        : <MesMapa day={day} setDay={setDay} today={today} pros={pros} colors={colors} onDay={openDay} />)}
      <div style={{ height: 16 }} />
    </div>
  );
}

interface ViewProps { day: string; today: string; pros: Professional[]; colors: Map<string, string> }
const soft = (c: string) => `color-mix(in srgb, ${c} 20%, var(--paper))`;
const evClass = (a: AgendaItem) => (a.status === 'completed' ? 'done' : a.status === 'in_service' || a.status === 'arrived' ? 'now' : a.status === 'pending' ? 'pend' : '');

// ---------------- Dia ----------------
function CalDia({ day, today, pros, colors, wide }: ViewProps & { wide: boolean }) {
  const nav = useNav();
  const toast = useToast();
  const me = useMe().data!;
  const s = me.salon.settings;
  const ag = useAgenda(day);
  const blocks = useBlocks().data ?? [];
  const { min: now } = useClock();
  const PX = wide ? 1.6 : 1.2;
  const list = (ag.data ?? []).filter(isLive);
  const hrs = pros.map((p) => hoursOf(day, s, p)).filter((h) => h.on);
  const salon = hoursOf(day, s);
  // o calendário mostra as 24 horas; o que está fora do expediente aparece esmaecido e abre já no horário de abertura
  const open = 0, close = 24 * 60;
  const focus = Math.min(...(hrs.length ? hrs.map((h) => h.ini) : [salon.on ? salon.ini : 9 * 60]), ...list.map((a) => toMin(a.time)));
  const hoursList: number[] = [];
  for (let h = open / 60; h < close / 60; h++) hoursList.push(h);
  const grid = useRef<HTMLDivElement>(null);
  const [ghost, setGhost] = useState<{ pro: string; m: number } | null>(null);

  // arrastar para mudar o horário (mouse: direto; toque: segurar e arrastar). Ao soltar, remarca, com "Desfazer".
  const act = useApptActions();
  const { drag, start: startDrag, suppressClick } = useEventDrag<AgendaItem>({
    canDrag: (a) => ['pending', 'confirmed'].includes(a.status),
    from: (a) => ({ day, pro: a.professionalId, m: toMin(a.time), dur: endMin(a) - toMin(a.time) }),
    place: (cx, cy, grab, dur) => {
      const g = grid.current;
      if (!g) return null;
      const rc = g.getBoundingClientRect();
      const colW = (rc.width - 46) / Math.max(1, pros.length);
      const idx = Math.min(pros.length - 1, Math.max(0, Math.floor((cx - rc.left - 46) / colW)));
      const m = Math.min(Math.max(open, close - dur), Math.max(open, Math.round((open + (cy - rc.top - grab) / PX) / 15) * 15));
      return { day, pro: pros[idx]!.id, m };
    },
    drop: (a, to) => { void act.reschedule(a, to.day, hhmm(to.m), to.pro, { date: a.date, time: a.time, professionalId: a.professionalId }); },
  });

  // abre perto de agora (hoje) ou do primeiro horário do dia
  useLayoutEffect(() => {
    const el = grid.current;
    if (!el) return;
    const target = day === today ? now - 60 : focus - 30;
    const top = el.getBoundingClientRect().top + window.scrollY + Math.max(0, target - open) * PX - 140;
    if (target > open + 60) window.scrollTo({ top: Math.max(0, top) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, ag.isSuccess]);

  const minAt = (e: React.MouseEvent<HTMLDivElement>) => open + Math.floor((e.clientY - e.currentTarget.getBoundingClientRect().top) / PX / 15) * 15;
  const tap = (e: React.MouseEvent<HTMLDivElement>, proId: string) => {
    const m = minAt(e);
    if (m < open || m >= close) return;
    if (day < today || (day === today && m < now)) return toast(day < today ? 'Esse dia já passou.' : 'Esse horário já passou.', 'warn');
    nav.open({ kind: 'novo', date: day, time: hhmm(m), professionalId: proId });
  };
  const cols = `46px repeat(${Math.max(1, pros.length)}, minmax(0, 1fr))`;
  // sem os horários, a grade vazia pareceria um dia livre: não mostra
  if (ag.data === undefined) return <AgState q={ag} />;
  if (!hrs.length && !list.length) {
    return <div className="ag-closed"><Icon name="cal" /><b>{salon.on ? 'Ninguém atende neste dia' : `Fechado ${dowIndex(day) === 0 ? 'aos domingos' : 'neste dia'}`}</b><p>Escolha outro dia nas setas ou veja a semana.</p></div>;
  }
  return (
    <div className="cal-day ag-day">
      <div className="cal-heads ag-heads" style={{ gridTemplateColumns: cols }}><span />
        {pros.map((p) => {
          // "livre agora" só quando não há ninguém na cadeira nem horário marcado para este minuto
          const chair = list.find((a) => a.professionalId === p.id && a.status === 'in_service');
          const slot = list.find((a) => a.professionalId === p.id && ['pending', 'confirmed', 'arrived'].includes(a.status) && toMin(a.time) <= now && endMin(a) > now);
          const busy = !!(chair || slot);
          const mineCount = list.filter((a) => a.professionalId === p.id).length;
          const nowTxt = chair ? 'atendendo' : slot ? `${first(slot.client)} agora` : 'livre agora';
          return <span key={p.id}><span className="av" style={{ background: colors.get(p.id) }}>{initials(p.name)}</span><span className="who"><b>{first(p.name)}</b><em className={busy ? 'busy' : 'free'}>{day === today ? nowTxt : `${mineCount} ${mineCount === 1 ? 'horário' : 'horários'}`}</em></span></span>;
        })}
      </div>
      <div ref={grid} className="cal-wrap cols" style={{ height: (close - open) * PX + 20, gridTemplateColumns: cols }}>
        <div className="cal-gutter">{hoursList.map((h) => <span key={h} style={{ top: (h * 60 - open) * PX }}>{h}h</span>)}</div>
        {hoursList.map((h) => (
          <div key={h} style={{ display: 'contents' }}>
            <div className="cal-hline" style={{ top: (h * 60 - open) * PX }} />
            {h * 60 < close && <div className="cal-hline half" style={{ top: (h * 60 - open + 30) * PX }} />}
          </div>
        ))}
        {pros.map((p) => {
          const h = hoursOf(day, s, p);
          return (
            <div key={p.id} className="cal-col" onClick={(e) => tap(e, p.id)} onMouseMove={(e) => wide && !drag && setGhost({ pro: p.id, m: minAt(e) })} onMouseLeave={() => setGhost(null)}
              aria-label={`Coluna de ${p.name}. Toque num horário vazio para agendar`}>
              {/* fora do expediente da parceira */}
              {h.on ? <>
                {h.ini > open && <div className="ag-off" style={{ top: 0, height: (h.ini - open) * PX }} />}
                {h.fim < close && <div className="ag-off" style={{ top: (h.fim - open) * PX, height: (close - h.fim) * PX }} />}
              </> : <div className="ag-off" style={{ top: 0, height: (close - open) * PX }}><span>folga</span></div>}
              {s.lunch.on && <div className="break" style={{ top: (toMin(s.lunch.ini) - open) * PX, height: (toMin(s.lunch.fim) - toMin(s.lunch.ini)) * PX }}>Almoço</div>}
              {blocksOn(day, blocks, p.id).map((b, i) => <button key={b.id + i} type="button" className="ag-block" aria-label={`Bloqueio: ${b.reason || 'Bloqueado'}. Toque para ver ou remover`} style={{ top: (Math.max(b.s, open) - open) * PX, height: (Math.min(b.e, close) - Math.max(b.s, open)) * PX }} onClick={(e) => { e.stopPropagation(); nav.open({ kind: 'bloqueio', professionalId: p.id, date: day }); }}><span>{b.reason || 'Bloqueado'}</span></button>)}
              {ghost && ghost.pro === p.id && ghost.m >= open && ghost.m < close && (day > today || (day === today && ghost.m >= now)) && (
                <div className="ag-ghost" style={{ top: (ghost.m - open) * PX, height: 30 * PX }}>+ {hhmm(ghost.m)}</div>
              )}
              {list.filter((a) => a.professionalId === p.id).map((a) => {
                const st = toMin(a.time), en = endMin(a);
                const c = colors.get(a.professionalId) ?? 'var(--rose)';
                return (
                  <div key={a.id} className={`ev ag-ev ${evClass(a)}${en - st < 45 ? ' short' : ''}${drag?.id === a.id ? ' dragsrc' : ''}`} role="button" tabIndex={0}
                    onPointerDown={(e) => startDrag(e, a)} onContextMenu={(e) => e.preventDefault()}
                    style={{ top: (st - open) * PX, height: (en - st) * PX - 3, background: soft(c), borderLeftColor: c }}
                    onClick={(e) => { e.stopPropagation(); if (suppressClick.current) return; nav.open({ kind: 'appt', id: a.id }); }}
                    onMouseMove={(e) => e.stopPropagation()}
                    onKeyDown={(e) => { if (e.key === 'Enter') nav.open({ kind: 'appt', id: a.id }); }}
                    aria-label={`${a.client}, ${a.time} a ${hhmm(en)}`}>
                    <strong>{a.status === 'completed' ? '✓ ' : ''}{a.client}</strong><span className="t">{a.time}–{hhmm(en)} · {a.service}</span>
                    {a.status === 'pending' && <span className="t pend">a confirmar</span>}
                    {a.notes && <span className="t obs">{a.notes}</span>}
                  </div>
                );
              })}
              {drag && drag.pro === p.id && (
                <div className="ev ag-ev lifted" aria-hidden="true" style={{ top: (drag.m - open) * PX, height: drag.dur * PX - 3, background: soft(colors.get(p.id) ?? 'var(--rose)'), borderLeftColor: colors.get(p.id) ?? 'var(--rose)' }}>
                  <strong>{hhmm(drag.m)}–{hhmm(drag.m + drag.dur)}</strong>
                </div>
              )}
            </div>
          );
        })}
        {day === today && now >= open && now <= close && <div className="now-line" style={{ top: (now - open) * PX }}><span>{hhmm(now)}</span></div>}
      </div>
    </div>
  );
}

// ---------------- Semana (computador): grade de horários, uma faixa por parceira em cada dia ----------------
function SemanaGrade({ day, today, pros, colors, onDay }: ViewProps & { onDay: (d: string) => void }) {
  const nav = useNav();
  const toast = useToast();
  const me = useMe().data!;
  const s = me.salon.settings;
  const ws = weekStart(day);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const week = useAgenda(ws, addDays(ws, 6));
  const blocks = useBlocks().data ?? [];
  const { min: now } = useClock();
  const items = (week.data ?? []).filter((a) => pros.some((p) => p.id === a.professionalId));
  const live = items.filter(isLive);
  const isOpen = (d: string) => pros.some((p) => hoursOf(d, s, p).on) || live.some((a) => a.date === d);
  // a grade vai das 9h às 19h e cresce para caber o expediente e os horários da semana
  const opens = days.filter(isOpen).flatMap((d) => pros.map((p) => hoursOf(d, s, p)).filter((h) => h.on));
  const start = 0, end = 24 * 60;
  const focus = Math.min(9 * 60, ...opens.map((h) => h.ini), ...live.map((a) => toMin(a.time)));
  const PX = 1.1;
  const hours: number[] = [];
  for (let h = start / 60; h < end / 60; h++) hours.push(h);
  const cols = `46px ${days.map((d) => (isOpen(d) ? 'minmax(0, 1fr)' : '74px')).join(' ')}`;
  const lanes = Math.max(1, pros.length);
  const body = useRef<HTMLDivElement>(null);
  // abre perto do horário de abertura (ou de agora, na semana atual), não à meia-noite
  useLayoutEffect(() => {
    const el = body.current;
    if (!el) return;
    const target = days.includes(today) ? Math.max(focus, now - 60) : focus;
    window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY + Math.max(0, target - 30 - start) * PX - 140) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, week.isSuccess]);
  const act = useApptActions();
  const { drag, start: startDrag, suppressClick } = useEventDrag<AgendaItem>({
    canDrag: (a) => ['pending', 'confirmed'].includes(a.status),
    from: (a) => ({ day: a.date, pro: a.professionalId, m: toMin(a.time), dur: endMin(a) - toMin(a.time) }),
    place: (cx, cy, grab, dur) => {
      const b = body.current;
      if (!b) return null;
      const cols = [...b.querySelectorAll<HTMLElement>('.wkg-col[data-day]')];
      if (!cols.length) return null;
      let col = cols[0]!;
      for (const c of cols) if (cx >= c.getBoundingClientRect().left) col = c;
      const r = col.getBoundingClientRect();
      const li = Math.min(lanes - 1, Math.max(0, Math.floor(((cx - r.left) / r.width) * lanes)));
      const m = Math.min(Math.max(start, end - dur), Math.max(start, Math.round((start + (cy - b.getBoundingClientRect().top - grab) / PX) / 15) * 15));
      return { day: col.dataset.day!, pro: pros[li]!.id, m };
    },
    drop: (a, to) => { void act.reschedule(a, to.day, hhmm(to.m), to.pro, { date: a.date, time: a.time, professionalId: a.professionalId }); },
  });
  const tap = (e: React.MouseEvent<HTMLDivElement>, d: string) => {
    const r = e.currentTarget.getBoundingClientRect();
    const m = start + Math.floor((e.clientY - r.top) / PX / 15) * 15;
    const pro = pros[Math.min(lanes - 1, Math.floor(((e.clientX - r.left) / r.width) * lanes))];
    if (!pro || m >= end) return;
    if (d < today || (d === today && m < now)) return toast('Esse horário já passou.', 'warn');
    nav.open({ kind: 'novo', date: d, time: hhmm(m), professionalId: pro.id });
  };
  if (week.data === undefined) return <AgState q={week} />;
  return (
    <div className="wkg" role="group" aria-label="Semana">
      <div className="wkg-head" style={{ gridTemplateColumns: cols }}>
        <span />
        {days.map((d) => {
          const st = dayStats(d, pros, items, s, blocks);
          return isOpen(d) ? (
            <button key={d} className={'wkg-day' + (d === today ? ' today' : '') + (d < today ? ' past' : '')} onClick={() => onDay(d)} title={`Abrir ${DOW_LONG[dowIndex(d)]!.toLowerCase()}, ${ddmm(d)}`}>
              <b>{DOW_SHORT[dowIndex(d)]} <span>{ddmm(d)}</span></b>
              <small>{st.count ? `${st.count} ${st.count === 1 ? 'horário' : 'horários'} · ${st.pct >= 95 ? 'lotado' : `${st.pct}%`}` : 'livre'}</small>
              <i className="ag-occ" aria-hidden="true"><i style={{ width: `${st.pct}%` }} /></i>
            </button>
          ) : <span key={d} className="wkg-day closed"><b>{DOW_SHORT[dowIndex(d)]} <span>{ddmm(d)}</span></b><small>fechado</small></span>;
        })}
      </div>
      <div ref={body} className="wkg-body" style={{ gridTemplateColumns: cols, height: (end - start) * PX + 12 }}>
        <div className="cal-gutter">{hours.map((h) => <span key={h} style={{ top: (h * 60 - start) * PX }}>{h}h</span>)}</div>
        {days.map((d) => {
          if (!isOpen(d)) return <div key={d} className="wkg-col closed" aria-label="Fechado" />;
          return (
            <div key={d} data-day={d} className={'wkg-col' + (d === today ? ' today' : '') + (d < today ? ' past' : '')} onClick={(e) => tap(e, d)} style={{ backgroundSize: `100% ${60 * PX}px` }}>
              {pros.map((p, li) => {
                const h = hoursOf(d, s, p);
                const left = `${(li * 100) / lanes}%`, width = `${100 / lanes}%`;
                return (
                  <div key={p.id} style={{ display: 'contents' }}>
                    {!h.on && <div className="ag-off lane" style={{ left, width, top: 0, height: (end - start) * PX }} />}
                    {h.on && h.ini > start && <div className="ag-off lane" style={{ left, width, top: 0, height: (h.ini - start) * PX }} />}
                    {h.on && h.fim < end && <div className="ag-off lane" style={{ left, width, top: (h.fim - start) * PX, height: (end - h.fim) * PX }} />}
                    {blocksOn(d, blocks, p.id).map((b, i) => <div key={i} className="ag-block lane" style={{ left, width, top: (Math.max(b.s, start) - start) * PX, height: (Math.min(b.e, end) - Math.max(b.s, start)) * PX }} title={b.reason} />)}
                    {freeOf(d, p, items, s, blocks).filter(([a, b]) => b - a >= 90).map(([a, b]) => (
                      <div key={a} className="ag-free lane" style={{ left, width, top: (a - start) * PX + 2, height: (b - a) * PX - 4 }} title={`${first(p.name)} livre das ${hhmm(a)} às ${hhmm(b)}`}><span>Livre</span></div>
                    ))}
                  </div>
                );
              })}
              {s.lunch.on && <div className="ag-lunch" style={{ top: (toMin(s.lunch.ini) - start) * PX, height: (toMin(s.lunch.fim) - toMin(s.lunch.ini)) * PX }} />}
              {live.filter((a) => a.date === d).map((a) => {
                const li = Math.max(0, pros.findIndex((p) => p.id === a.professionalId));
                const st = toMin(a.time), en = endMin(a);
                const c = colors.get(a.professionalId) ?? 'var(--rose)';
                return (
                  <button key={a.id} className={`wkg-ev ${evClass(a)}${drag?.id === a.id ? ' dragsrc' : ''}`} onPointerDown={(e) => startDrag(e, a)} onContextMenu={(e) => e.preventDefault()} style={{ left: `calc(${(li * 100) / lanes}% + 2px)`, width: `calc(${100 / lanes}% - 4px)`, top: (st - start) * PX + 1, height: Math.max(20, (en - st) * PX - 2), background: soft(c), borderLeftColor: c }}
                    onClick={(e) => { e.stopPropagation(); if (suppressClick.current) return; nav.open({ kind: 'appt', id: a.id }); }}
                    title={`${a.time}–${hhmm(en)} · ${a.client} · ${a.service} · ${proName(pros, a.professionalId)} · ${ST[a.status]}`}
                    aria-label={`${a.client}, ${a.time} a ${hhmm(en)}, ${a.service}, com ${proName(pros, a.professionalId)}`}>
                    <b>{a.status === 'completed' ? '✓ ' : ''}{lanes > 1 ? shortName(a.client) : a.client}</b>{en - st >= 40 && <span>{a.time} · {a.service}</span>}
                  </button>
                );
              })}
              {drag && drag.day === d && (() => {
                const li = Math.max(0, pros.findIndex((p) => p.id === drag.pro)), c = colors.get(drag.pro) ?? 'var(--rose)';
                return <div className="wkg-ev lifted" aria-hidden="true" style={{ left: `calc(${(li * 100) / lanes}% + 2px)`, width: `calc(${100 / lanes}% - 4px)`, top: (drag.m - start) * PX + 1, height: Math.max(20, drag.dur * PX - 2), background: soft(c), borderLeftColor: c }}><b>{hhmm(drag.m)}</b></div>;
              })()}
              {d === today && now >= start && now <= end && <div className="now-line" style={{ top: (now - start) * PX }} />}
            </div>
          );
        })}
      </div>
      {pros.length > 1 && <p className="ag-hint">Cada dia tem uma faixa por parceira, na ordem do filtro. Clique num espaço vazio para agendar ali; clique no dia para abrir o dia inteiro.</p>}
    </div>
  );
}

// ---------------- Semana (celular): lista contínua, separada por dia, com os horários livres ----------------
function SemanaLista({ day, setDay, today, pros, colors }: ViewProps & { setDay: (d: string) => void }) {
  const nav = useNav();
  const me = useMe().data!;
  const s = me.salon.settings;
  const ws = weekStart(day);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const week = useAgenda(ws, addDays(ws, 6));
  const blocks = useBlocks().data ?? [];
  const items = (week.data ?? []).filter((a) => pros.some((p) => p.id === a.professionalId));
  const refs = useRef(new Map<string, HTMLElement>());
  const single = pros.length === 1 ? pros[0]! : null;
  const jump = (d: string) => {
    setDay(d);
    const el = refs.current.get(d);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 150, behavior: 'smooth' });
  };
  if (week.data === undefined) return <AgState q={week} />;
  return (
    <div className="wkl">
      <div className="wkl-strip" role="tablist" aria-label="Dias da semana">
        {days.map((d) => {
          const st = dayStats(d, pros, items, s, blocks);
          return (
            <button key={d} role="tab" aria-selected={day === d} className={(day === d ? 'sel' : '') + (d === today ? ' today' : '') + (!st.open ? ' closed' : '')} onClick={() => jump(d)}
              aria-label={`${DOW_LONG[dowIndex(d)]} ${ddmm(d)}${st.open ? `, ${st.count} horários, ${st.pct}% ocupado` : ', fechado'}`}>
              <small>{DOW_SHORT[dowIndex(d)]}</small><b>{ddmm(d)}</b><em>{st.open ? st.count || '·' : '—'}</em>
              {st.open && <i className="ag-occ" aria-hidden="true"><i style={{ width: `${st.pct}%` }} /></i>}
            </button>
          );
        })}
      </div>
      {days.map((d) => {
        const st = dayStats(d, pros, items, s, blocks);
        const list = items.filter((a) => a.date === d).sort((x, y) => x.time.localeCompare(y.time));
        const head = `${DOW_LONG[dowIndex(d)]}, ${ddmm(d)}`;
        if (!st.open && !list.length) return <div key={d} ref={(el) => { if (el) refs.current.set(d, el); }} className="wkl-closed">{head} · fechado</div>;
        const free = pros.map((p) => ({ p, m: freeOf(d, p, items, s, blocks).reduce((t, [a, b]) => t + b - a, 0) })).filter((x) => x.m > 0);
        // os buracos aparecem na lista quando se vê uma parceira só (com várias, o cabeçalho diz quanto cada uma tem livre)
        const gaps = single ? freeOf(d, single, items, s, blocks).filter(([a, b]) => b - a >= 45) : [];
        const rows: Array<{ k: string; t: number; el: React.ReactNode }> = list.map((a) => ({
          k: a.id, t: toMin(a.time),
          el: (
            <button className={'wkl-row' + (isLive(a) ? '' : ' off')} onClick={() => nav.open({ kind: 'appt', id: a.id })}>
              <span className="t">{a.time}</span>
              <span className="w"><b>{a.client}</b><small>{a.service}{pros.length > 1 ? ` · ${proName(pros, a.professionalId)}` : ''}{a.status !== 'confirmed' ? ` · ${ST[a.status]}` : ''}</small></span>
              <i className="bar" style={{ background: colors.get(a.professionalId) }} aria-hidden="true" />
            </button>
          ),
        }));
        for (const [a, b] of gaps) rows.push({
          k: `g${a}`, t: a,
          el: <button className="wkl-row gap" onClick={() => nav.open({ kind: 'novo', date: d, time: hhmm(a), professionalId: single!.id })}><span className="t">{hhmm(a)}</span><span className="w">Livre até {hhmm(b)} · {durShort(b - a)}</span><em>Encaixar</em></button>,
        });
        rows.sort((x, y) => x.t - y.t || (x.k.startsWith('g') ? 1 : -1));
        return (
          <section key={d} ref={(el) => { if (el) refs.current.set(d, el); }} className={'wkl-day' + (d === today ? ' today' : '')}>
            <h2>{head}{d === today && <em>hoje</em>}<small>{list.filter(isLive).length ? `${list.filter(isLive).length} ${list.filter(isLive).length === 1 ? 'horário' : 'horários'}` : 'nenhum horário'}{free.length ? ` · livre ${free.map((x) => (pros.length > 1 ? `${first(x.p.name)} ${durShort(x.m)}` : durShort(x.m))).join(', ')}` : ''}</small></h2>
            {rows.length ? rows.map((r) => <div key={r.k}>{r.el}</div>) : null}
            {!list.length && d >= today && st.open && <button className="wkl-row gap" onClick={() => nav.open({ kind: 'novo', date: d })}><span className="t" /><span className="w">Dia livre</span><em>Agendar</em></button>}
          </section>
        );
      })}
    </div>
  );
}

// ---------------- Mês ----------------
function useMonth(day: string, pros: Professional[]) {
  const me = useMe().data!;
  const firstDay = `${day.slice(0, 7)}-01`;
  const start = weekStart(firstDay);
  const lastDay = addDays(`${addDays(firstDay, 32).slice(0, 7)}-01`, -1);
  const end = addDays(weekStart(lastDay), 6);
  const ag = useAgenda(start, end);
  const blocks = useBlocks().data ?? [];
  const items = (ag.data ?? []).filter((a) => pros.some((p) => p.id === a.professionalId));
  const days: string[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  return { days, items, blocks, s: me.salon.settings as Settings, month: day.slice(0, 7), q: ag };
}
// enquanto a agenda não chegou (ou falhou), nenhuma grade vazia se passa por horário livre
function AgState({ q }: { q: { isError: boolean; error: unknown; refetch: () => unknown } }) {
  return q.isError ? <Failed error={q.error} retry={() => q.refetch()} /> : <Skeleton rows={5} />;
}
const WD = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function MesGrade({ day, today, pros, colors, onDay }: ViewProps & { onDay: (d: string) => void }) {
  const nav = useNav();
  const { days, items, blocks, s, month, q } = useMonth(day, pros);
  if (q.data === undefined) return <AgState q={q} />;
  return (
    <div className="mog">
      <div className="mog-wd">{WD.map((w) => <span key={w}>{w}</span>)}</div>
      <div className="mog-grid">
        {days.map((d) => {
          const st = dayStats(d, pros, items, s, blocks as Block[]);
          const list = items.filter((a) => a.date === d && isLive(a)).sort((x, y) => x.time.localeCompare(y.time));
          const out = d.slice(0, 7) !== month;
          return (
            <div key={d} className={'mog-cell' + (out ? ' out' : '') + (!st.open ? ' closed' : '') + (d === today ? ' today' : '') + (d < today ? ' past' : '')}
              onClick={() => onDay(d)}>
              {/* o dia abre pelo número (botão de verdade, para teclado e leitor de tela); clicar no resto da célula também abre */}
              <button className="mog-n" onClick={(e) => { e.stopPropagation(); onDay(d); }}
                aria-label={`${DOW_LONG[dowIndex(d)]}, ${ddmm(d)} de ${monthName(d)}: ${st.open ? `${st.count} horários` : 'fechado'}`}>
                <b>{ddmm(d)}</b><small>{!st.open ? 'fechado' : st.count ? `${st.count} ${st.count === 1 ? 'horário' : 'horários'}` : d >= today ? 'livre' : ''}</small></button>
              {list.slice(0, 3).map((a) => <span key={a.id} className="mog-ln" style={{ background: soft(colors.get(a.professionalId) ?? 'var(--rose)') }}>{a.time} {first(a.client)}</span>)}
              {list.length > 3 && <span className="mog-more">+{list.length - 3} {list.length - 3 === 1 ? 'horário' : 'horários'}</span>}
              {st.open && <i className="ag-occ" aria-hidden="true"><i style={{ width: `${st.pct}%` }} /></i>}
              {st.open && d >= today && <button className="mog-add" aria-label={`Agendar em ${ddmm(d)}`} title="Agendar neste dia" onClick={(e) => { e.stopPropagation(); nav.open({ kind: 'novo', date: d }); }}><Icon name="plus" /></button>}
            </div>
          );
        })}
      </div>
      <p className="ag-hint">A barra embaixo de cada dia mostra quanto da agenda já está ocupado. Clique no dia para abrir; no + para agendar direto.</p>
    </div>
  );
}

function MesMapa({ day, setDay, today, pros, colors, onDay }: ViewProps & { setDay: (d: string) => void; onDay: (d: string) => void }) {
  const nav = useNav();
  const { days, items, blocks, s, month, q } = useMonth(day, pros);
  const list = items.filter((a) => a.date === day).sort((x, y) => x.time.localeCompare(y.time));
  const sel = dayStats(day, pros, items, s, blocks as Block[]);
  if (q.data === undefined) return <AgState q={q} />;
  return (
    <div className="mom">
      <div className="mom-grid">
        {WD.map((w) => <span key={w} className="mom-wd">{w}</span>)}
        {days.map((d) => {
          const st = dayStats(d, pros, items, s, blocks as Block[]);
          const out = d.slice(0, 7) !== month;
          return (
            <button key={d} className={`mom-d ${st.open ? heat(st.pct, st.count) : 'closed'}${out ? ' out' : ''}${d === today ? ' today' : ''}${d === day ? ' sel' : ''}`} onClick={() => setDay(d)}
              aria-pressed={d === day} aria-label={`${ddmm(d)} de ${monthName(d)}: ${st.open ? `${st.count} horários, ${st.pct}% ocupado` : 'fechado'}`}>
              <b>{ddmm(d)}</b><small>{st.open && st.count ? st.count : ''}</small>
            </button>
          );
        })}
      </div>
      <div className="mom-legend" aria-hidden="true"><span><i className="h1" />pouco</span><span><i className="h3" />cheio</span><span><i className="full" />lotado</span><span><s>4</s> fechado</span></div>
      <section className="wkl-day mom-day">
        <h2>{DOW_LONG[dowIndex(day)]}, {ddmm(day)}{day === today && <em>hoje</em>}<small>{!sel.open ? 'fechado' : list.filter(isLive).length ? `${list.filter(isLive).length} horários · ${sel.pct}% ocupado` : 'nenhum horário'}</small></h2>
        {list.map((a) => (
          <button key={a.id} className={'wkl-row' + (isLive(a) ? '' : ' off')} onClick={() => nav.open({ kind: 'appt', id: a.id })}>
            <span className="t">{a.time}</span><span className="w"><b>{a.client}</b><small>{a.service}{pros.length > 1 ? ` · ${proName(pros, a.professionalId)}` : ''}{a.status !== 'confirmed' ? ` · ${ST[a.status]}` : ''}</small></span>
            <i className="bar" style={{ background: colors.get(a.professionalId) }} aria-hidden="true" />
          </button>
        ))}
        <div className="mom-acts">
          <button className="cta ghost sm" onClick={() => onDay(day)}>Abrir o dia</button>
          {sel.open && day >= today && <button className="cta sm" onClick={() => nav.open({ kind: 'novo', date: day })}>Agendar</button>}
        </div>
      </section>
    </div>
  );
}
